import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    FileCode2, Upload, ClipboardPaste, Loader2, Plus, Trash2, ShieldAlert,
    AlertTriangle, ListChecks, RefreshCcw, FileSearch,
} from "lucide-react";
import { toast } from "sonner";
import { Panel, StatTile } from "@/components/common/Panel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { CodeFindingCard } from "@/components/codereview/CodeFindingCard";
import { RicefwClearancePanel } from "@/components/codereview/RicefwClearancePanel";

const SEVS = ["critical", "high", "medium", "low", "info"];
const SAMPLE = `REPORT zfi_vendor_payment.

START-OF-SELECTION.
  SELECT * FROM lfa1 INTO TABLE @DATA(lt_vendors).

  LOOP AT lt_vendors INTO DATA(ls_vendor).
    UPDATE lfbk SET bankn = ls_vendor-lifnr WHERE lifnr = ls_vendor-lifnr.
  ENDLOOP.

  SELECT * FROM (lv_table) INTO TABLE @DATA(lt_dyn) WHERE (lv_where).

  CALL FUNCTION 'RFC_ABAP_INSTALL_AND_RUN' DESTINATION lv_dest.`;

export default function CodeReview() {
    const [reference, setReference] = useState({});
    const [sessions, setSessions] = useState([]);
    const [active, setActive] = useState(null);       // full session view
    const [loading, setLoading] = useState(true);
    const [pasteCode, setPasteCode] = useState("");
    const [reviewName, setReviewName] = useState("");
    const [analyzing, setAnalyzing] = useState(false);
    const [severityFilter, setSeverityFilter] = useState("all");
    const [showIntake, setShowIntake] = useState(false);

    const loadSessions = useCallback(async () => {
        try {
            const { data } = await api.get("/code-review/sessions");
            setSessions(data.sessions);
        } catch (e) { /* gate handles 403 */ }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [ref] = await Promise.all([api.get("/code-review/reference")]);
                setReference(ref.data);
                await loadSessions();
            } catch (e) {
                console.warn("Code review init failed", e);
            } finally { setLoading(false); }
        })();
    }, [loadSessions]);

    const openSession = async (id) => {
        try {
            const { data } = await api.get(`/code-review/sessions/${id}`);
            setActive(data);
            setShowIntake(false);
        } catch (e) { toast.error("Could not open session"); }
    };

    const analyzePaste = async () => {
        if (!pasteCode.trim()) { toast.error("Paste some ABAP source first"); return; }
        setAnalyzing(true);
        try {
            const { data } = await api.post("/code-review/analyze", { name: reviewName, source_code: pasteCode });
            toast.success(`Analyzed — ${data.findings.length} finding(s)`);
            setActive(data); setShowIntake(false); setPasteCode(""); setReviewName("");
            await loadSessions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Analysis failed");
        } finally { setAnalyzing(false); }
    };

    const analyzeUpload = async (file) => {
        if (!file) return;
        setAnalyzing(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("name", reviewName || file.name);
            const { data } = await api.post("/code-review/analyze-upload", fd);
            toast.success(`Analyzed ${file.name} — ${data.findings.length} finding(s)`);
            setActive(data); setShowIntake(false); setReviewName("");
            await loadSessions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload analysis failed");
        } finally { setAnalyzing(false); }
    };

    const deleteSession = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm("Delete this code review session?")) return;
        try {
            await api.delete(`/code-review/sessions/${id}`);
            if (active?.id === id) setActive(null);
            await loadSessions();
            toast.success("Deleted");
        } catch (err) { toast.error("Delete failed"); }
    };

    const filteredFindings = useMemo(() => {
        if (!active) return [];
        if (severityFilter === "all") return active.findings;
        return active.findings.filter((f) => f.severity === severityFilter);
    }, [active, severityFilter]);

    if (loading) {
        return <Panel eyebrow="SAP Security" title="Code Review / RICEFW Clearance"><p className="text-sm text-slate-500 font-mono py-6">Loading…</p></Panel>;
    }

    return (
        <div className="space-y-6" data-testid={TID.crPage}>
            {/* Header */}
            <div className="panel-elevated relative overflow-hidden p-6">
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
                <div className="flex items-center justify-between gap-4 relative flex-wrap">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_20px_rgb(var(--accent-1-rgb) / 0.45)]">
                            <FileCode2 size={22} className="text-slate-950" strokeWidth={2.5} />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl text-white tracking-wide">SAP Authorization Code Review &amp; RICEFW Clearance</h1>
                            <p className="text-sm text-slate-400 mt-0.5 max-w-2xl">
                                Authorization-centric ABAP review for RICEFW transport clearance — AUTHORITY-CHECK correctness, sensitive data / RFC / OData / dynamic-code risks, SU24 &amp; PFCG impact, and an evidence-backed clearance verdict. Rule-set {reference.ruleset_version}.
                            </p>
                        </div>
                    </div>
                    <button data-testid={TID.crNewBtn} onClick={() => { setShowIntake(true); setActive(null); }} className="btn-primary sheen inline-flex items-center gap-2">
                        <Plus size={14} /> New Review
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {/* Left: session list */}
                <div className="col-span-12 lg:col-span-3">
                    <Panel eyebrow="Reviews" title={`Sessions (${sessions.length})`}>
                        <div className="space-y-1.5 max-h-[620px] overflow-y-auto">
                            {sessions.length === 0 && <p className="text-[11px] font-mono text-slate-500">No reviews yet — start one with "New Review".</p>}
                            {sessions.map((s) => (
                                <button
                                    key={s.id}
                                    data-testid={TID.crSessionRow(s.id)}
                                    onClick={() => openSession(s.id)}
                                    className={`w-full text-left panel px-2.5 py-2 rounded-[4px] transition-colors ${active?.id === s.id ? "border-cyan-500/40" : "hover:border-white/15"}`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs text-white font-medium truncate">{s.name}</span>
                                        <span onClick={(e) => deleteSession(s.id, e)} data-testid={TID.crSessionDeleteBtn(s.id)} className="text-slate-600 hover:text-rose-400 shrink-0"><Trash2 size={12} /></span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        {s.verdict && <span className="text-[9px] font-mono text-slate-400">{s.verdict}</span>}
                                        {s.open_critical > 0 && <span className="text-[9px] font-mono text-rose-400">{s.open_critical}C</span>}
                                        {s.open_high > 0 && <span className="text-[9px] font-mono text-orange-400">{s.open_high}H</span>}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </Panel>
                </div>

                {/* Right: intake OR active session */}
                <div className="col-span-12 lg:col-span-9 space-y-6">
                    {(showIntake || (!active && sessions.length === 0)) && (
                        <Panel eyebrow="Input" title="Analyze ABAP Source">
                            <input
                                value={reviewName} onChange={(e) => setReviewName(e.target.value)}
                                placeholder="Review name (optional)" className="field w-full mb-3"
                            />
                            <Tabs defaultValue="paste">
                                <TabsList>
                                    <TabsTrigger value="paste"><ClipboardPaste size={13} className="mr-1.5" /> Paste ABAP</TabsTrigger>
                                    <TabsTrigger value="upload"><Upload size={13} className="mr-1.5" /> Upload File</TabsTrigger>
                                </TabsList>
                                <TabsContent value="paste">
                                    <textarea
                                        data-testid={TID.crPasteInput}
                                        value={pasteCode} onChange={(e) => setPasteCode(e.target.value)}
                                        rows={14} spellCheck={false}
                                        placeholder="Paste ABAP source (REPORT / CLASS / FUNCTION / DPC_EXT method)…"
                                        className="field w-full font-mono text-[12px] resize-y"
                                    />
                                    <div className="flex items-center gap-2 mt-3">
                                        <button data-testid={TID.crAnalyzePasteBtn} onClick={analyzePaste} disabled={analyzing} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50">
                                            {analyzing ? <Loader2 size={13} className="animate-spin" /> : <FileSearch size={13} />} Analyze
                                        </button>
                                        <button onClick={() => setPasteCode(SAMPLE)} className="btn-ghost text-[11px]">Load sample vulnerable code</button>
                                    </div>
                                </TabsContent>
                                <TabsContent value="upload">
                                    <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-white/15 rounded-[4px] p-10 cursor-pointer hover:border-cyan-500/40 transition-colors">
                                        {analyzing ? <Loader2 size={22} className="animate-spin text-cyan-400" /> : <Upload size={22} className="text-slate-500" />}
                                        <span className="text-sm text-slate-300">Drop or choose a .abap / .txt file</span>
                                        <span className="text-[10px] font-mono text-slate-500">Same parser &amp; rules as paste — identical source yields identical findings</span>
                                        <input
                                            data-testid={TID.crUploadInput}
                                            type="file" accept=".abap,.txt" className="hidden"
                                            onChange={(e) => { analyzeUpload(e.target.files?.[0]); e.target.value = ""; }}
                                        />
                                    </label>
                                </TabsContent>
                            </Tabs>
                        </Panel>
                    )}

                    {!showIntake && !active && sessions.length > 0 && (
                        <Panel eyebrow="Workbench" title="Pick a review — or start a new one" testId="cr-empty-state">
                            <div className="py-8 flex flex-col items-center text-center gap-4">
                                <div className="relative">
                                    <div className="w-16 h-16 rounded-full border border-cyan-500/25 flex items-center justify-center animate-float">
                                        <FileSearch size={24} className="text-cyan-400/80" />
                                    </div>
                                    <span className="absolute inset-0 rounded-full border border-cyan-500/10 scale-150 animate-spin-slow" />
                                </div>
                                <div className="max-w-md">
                                    <p className="text-sm text-slate-300">
                                        Select one of the {sessions.length} saved reviews on the left to reopen its findings,
                                        RICEFW metadata and clearance verdict — or paste fresh ABAP to start a new review.
                                    </p>
                                    <p className="text-[11px] font-mono text-slate-500 mt-3 uppercase tracking-wider">
                                        Rule-set UTSAVA-SEC-CR 2026.02 · deterministic · offline
                                    </p>
                                </div>
                                <button
                                    data-testid="cr-empty-new-btn"
                                    onClick={() => setShowIntake(true)}
                                    className="btn-primary inline-flex items-center gap-1.5"
                                >
                                    <Plus size={14} /> New Review
                                </button>
                            </div>
                        </Panel>
                    )}

                    {active && (
                        <>
                            {/* KPI tiles */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <StatTile testId={TID.crKpiTile("findings")} label="Findings" value={active.findings.length} sub="authorization & access" icon={ShieldAlert} tone="amber" />
                                <StatTile testId={TID.crKpiTile("critical")} label="Critical" value={active.severity_counts?.critical || 0} sub="must fix before clearance" icon={AlertTriangle} tone="red" />
                                <StatTile testId={TID.crKpiTile("statements")} label="Statements" value={active.statement_count} sub={`${active.line_count} lines`} icon={ListChecks} tone="cyan" />
                                <StatTile testId={TID.crKpiTile("atc")} label="ATC Findings" value={active.atc?.total || 0} sub={active.atc ? `${active.atc.security_relevant_count} security-relevant` : "none imported"} icon={FileSearch} tone="slate" />
                            </div>

                            <div className="grid grid-cols-12 gap-6">
                                <div className="col-span-12 xl:col-span-7 space-y-4">
                                    <Panel
                                        eyebrow="Findings"
                                        title={`Authorization & Access-Control Findings (${filteredFindings.length})`}
                                        action={
                                            <div className="flex items-center gap-1">
                                                <button data-testid={TID.crSeverityFilter("all")} onClick={() => setSeverityFilter("all")} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${severityFilter === "all" ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" : "text-slate-400 border-white/10"}`}>all</button>
                                                {SEVS.map((s) => (
                                                    <button key={s} data-testid={TID.crSeverityFilter(s)} onClick={() => setSeverityFilter(s)} className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${severityFilter === s ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" : "text-slate-400 border-white/10"}`}>
                                                        {s[0].toUpperCase()}{active.severity_counts?.[s] ? ` ${active.severity_counts[s]}` : ""}
                                                    </button>
                                                ))}
                                            </div>
                                        }
                                    >
                                        <div className="space-y-2 max-h-[600px] overflow-y-auto">
                                            {filteredFindings.length === 0 && <p className="text-[11px] font-mono text-slate-500 py-4">No findings at this severity — the code passes these authorization checks.</p>}
                                            {filteredFindings.map((f) => (
                                                <CodeFindingCard
                                                    key={f.key} finding={f} sessionId={active.id}
                                                    statuses={reference.finding_statuses || []}
                                                    atcConfirmed={(active.atc?.correlation?.cross_confirmed_keys || []).includes(f.key)}
                                                    onUpdated={setActive}
                                                />
                                            ))}
                                        </div>
                                    </Panel>

                                    {/* Re-analyze after fix */}
                                    <ReanalyzePanel session={active} onUpdated={setActive} />
                                </div>

                                <div className="col-span-12 xl:col-span-5">
                                    <RicefwClearancePanel session={active} reference={reference} onUpdated={setActive} />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function ReanalyzePanel({ session, onUpdated }) {
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const run = async () => {
        const src = code.trim() || session.source_code;
        setBusy(true);
        try {
            const { data } = await api.post(`/code-review/sessions/${session.id}/reanalyze`, { source_code: src });
            toast.success(`Re-analyzed — ${data.findings.length} finding(s) remain`);
            onUpdated?.(data); setCode("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Re-analysis failed");
        } finally { setBusy(false); }
    };
    return (
        <Panel eyebrow="Iterate" title="Re-analyze After Fix">
            <p className="text-[11px] text-slate-400 mb-2">Paste the corrected source to re-run the review. Resolved-finding decisions carry over where the finding still exists.</p>
            <textarea
                data-testid={TID.crReanalyzeInput}
                value={code} onChange={(e) => setCode(e.target.value)} rows={6} spellCheck={false}
                placeholder="Paste corrected ABAP (leave empty to re-run on the current source)…"
                className="field w-full font-mono text-[12px] resize-y"
            />
            <button data-testid={TID.crReanalyzeBtn} onClick={run} disabled={busy} className="btn-primary mt-3 inline-flex items-center gap-1.5 disabled:opacity-50">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCcw size={13} />} Re-analyze
            </button>
        </Panel>
    );
}
